import { motion } from "framer-motion";
import { PORTFOLIO } from "@/data/content";
import { TEST_IDS } from "@/constants/testIds";
import LiteYouTube from "./LiteYouTube";

// Bento layout: mixing 9:16 phone frames and 16:9 wide frames
// We split into two rows for a controlled asymmetrical bento.

function PhoneClip({ item, index, className = "" }) {
  return (
    <motion.div
      data-testid={TEST_IDS.portfolio.item(index)}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1], delay: (index % 3) * 0.08 }}
      className={`block group ${className}`}
    >
      <div className="phone-frame">
        <div className="screen">
          <LiteYouTube
            videoId={item.youtubeId}
            title={item.title}
            overlay={
              <div className="absolute inset-0 p-4 flex flex-col justify-between text-white pointer-events-none">
                <div className="flex justify-between items-start">
                  <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase bg-white/15 backdrop-blur px-2 py-1 rounded-full">
                    {item.aspect}
                  </span>
                  <span className="font-mono-ui text-[10px] tracking-widest">{item.duration}</span>
                </div>
                <div>
                  <div className="font-serif-display text-lg leading-tight max-w-[22ch]">
                    &ldquo;{item.title}&rdquo;
                  </div>
                  <div className="mt-1 text-[11px] tracking-widest uppercase opacity-80">
                    {item.church}
                  </div>
                </div>
              </div>
            }
          />
        </div>
      </div>
    </motion.div>
  );
}

function WideClip({ item, index, className = "" }) {
  return (
    <motion.div
      data-testid={TEST_IDS.portfolio.item(index)}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
      className={`block group ${className}`}
    >
      <div className="wide-frame">
        <LiteYouTube
          videoId={item.youtubeId}
          title={item.title}
          overlay={
            <div className="absolute inset-0 p-6 flex flex-col justify-between text-white pointer-events-none">
              <div className="flex justify-between items-start">
                <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase bg-white/15 backdrop-blur px-2 py-1 rounded-full">
                  {item.aspect}
                </span>
                <span className="font-mono-ui text-[10px] tracking-widest">{item.duration}</span>
              </div>
              <div className="flex items-end justify-between gap-6">
                <div>
                  <div className="font-serif-display text-2xl md:text-3xl leading-tight max-w-[26ch]">
                    &ldquo;{item.title}&rdquo;
                  </div>
                  <div className="mt-1 text-[11px] tracking-widest uppercase opacity-80">
                    {item.church}
                  </div>
                </div>
              </div>
            </div>
          }
        />
      </div>
    </motion.div>
  );
}

export default function Portfolio() {
  const [p1, p2, w1, p3, p4, w2, w3] = PORTFOLIO;

  return (
    <section
      id="work"
      data-testid={TEST_IDS.portfolio.root}
      className="py-24 md:py-40 border-t border-[#E5E1DA] bg-[#FDFCFB]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="mb-16 md:mb-24 grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-2 eyebrow">Selected work</div>
          <h2 className="md:col-span-8 font-serif-display font-black tracking-tight text-[clamp(38px,6vw,84px)] leading-[0.95]">
            Clips that felt worth
            <br />
            <span className="italic text-[#E07A5F]">a Sunday morning.</span>
          </h2>
          <div className="md:col-span-2 text-right hidden md:block font-mono-ui text-[11px] tracking-[0.24em] text-[#787672] uppercase">
            {PORTFOLIO.length.toString().padStart(2, "0")} &middot; Clips
          </div>
        </div>

        {/* Row 1 — two phones + one wide */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <div className="md:col-span-4">
            <PhoneClip item={p1} index={1} />
          </div>
          <div className="md:col-span-4 md:mt-16">
            <PhoneClip item={p2} index={2} />
          </div>
          <div className="md:col-span-4 md:mt-8">
            <WideClip item={w1} index={3} />
            <div className="mt-8">
              <div className="eyebrow mb-2">Case note &middot; {w1.church}</div>
              <p className="text-[15px] text-[#1C1C1A]/75 leading-relaxed">
                Sixty-minute Sunday sermon &rarr; a three-clip highlight reel
                and two vertical shorts. Delivered in under 48 hours.
              </p>
            </div>
          </div>
        </div>

        {/* Row 2 — wide + two phones */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mt-6 md:mt-8">
          <div className="md:col-span-5 md:mt-8">
            <WideClip item={w2} index={4} />
          </div>
          <div className="md:col-span-3 md:mt-24">
            <PhoneClip item={p3} index={5} />
          </div>
          <div className="md:col-span-4">
            <PhoneClip item={p4} index={6} />
          </div>
        </div>

        {/* Row 3 — full-width feature */}
        <div className="mt-6 md:mt-16">
          <WideClip item={w3} index={7} className="feature-wide" />
        </div>
      </div>
    </section>
  );
}
